import * as DateFns from "date-fns";
import {
  DateTypeConstraints,
  NumberTypeConstraints,
} from "src/scenes/orgAdmin/formTemplates/ContentTab/V1/formik/types";
import { BaseAddress } from "src/schemas/Address";
import { LocationBoundariesFilterRuleOptions } from "src/schemas/formTemplateFilters";
import * as AF from "src/types/formTemplate";
import { VisibilitySchema } from "src/types/formTemplate/visibility";
import * as GQL from "src/types/graphql";
import { isLocationBoundariesFilter } from "../formTemplateFilters";
import { isValidDate } from "../formValidations";
import { Answer } from "./";
import { isQuestionApplicable } from "./generalSection";
import { SelectedSchoolIds } from "./generalSection/question";
import * as Visibility from "./visibility";

export function getQuestionIdsInOrder(
  sections: AF.Sections<AF.WithId> | undefined
) {
  return (
    sections
      ?.flatMap((section) =>
        section?.type === "PreRankingSection" ||
        section?.type === "GeneralSection"
          ? getCompleteQuestions(section.questions)
          : []
      )
      .map((q) => q.id) ?? []
  );
}
export type QuestionFilter =
  | {
      filterBy: "school";
      schoolIdSet: Set<uuid>;
    }
  | {
      filterBy: "none";
    };

export function getQuestionIdsAndLabelsInOrder(
  sections: AF.Sections<AF.WithId> | undefined,
  filter: QuestionFilter
) {
  const completeQuestions = sections?.flatMap((section) =>
    section?.type === "PreRankingSection" || section?.type === "GeneralSection"
      ? getCompleteQuestions(section.questions, filter)
      : []
  );
  if (!completeQuestions) {
    return [];
  }

  return completeQuestions.map((q) => ({
    id: q.id,
    label: q.question,
    isArchived: q.isArchived ?? false,
  }));
}

export function getFormCompleteQuestions(
  formTemplate: AF.FormTemplate<AF.WithId>,
  filter: QuestionFilter = { filterBy: "none" }
): AF.Question<AF.WithId>[] {
  let questions: AF.Question<AF.WithId>[] = [];
  for (const section of formTemplate.sections) {
    if (
      section === undefined ||
      section.type === AF.SchoolRankingSectionType ||
      section.type === AF.DisclaimerSectionType
    ) {
      continue;
    }

    questions = questions.concat(
      getCompleteQuestions(section.questions, filter)
    );
  }

  return questions;
}

export function getCompleteQuestions(
  questions: readonly AF.Question<AF.WithId>[],
  filter: QuestionFilter = { filterBy: "none" }
): readonly AF.Question<AF.WithId>[] {
  const completeQuestions = questions.reduce(
    (
      accumulatedQuestions: AF.Question<AF.WithId>[],
      question: AF.Question<AF.WithId>
    ) => {
      const flattedQuestions = flattenQuestion(question);
      return [...accumulatedQuestions, ...flattedQuestions];
    },
    []
  );

  switch (filter.filterBy) {
    case "school": {
      // filter by schools
      const schoolVisibilityContext: Visibility.SchoolVisibilityContext = {
        visibilityMap: Visibility.toVisibilityMap(completeQuestions),
        selectedSchoolsSet: filter.schoolIdSet,
      };
      return completeQuestions.filter((q) => {
        const visibility = schoolVisibilityContext.visibilityMap.get(q.id);
        if (!visibility) {
          return true;
        }

        // Check if this question has school-based visibility rules
        // If it does, we need to verify that the school rule passes.
        // For mixed rules (questionKey + school), we can't evaluate questionKey rules
        // without form answers in export context, so we check that the school rule
        // would pass. The questionKey rule will be evaluated at runtime when form
        // answers are available.
        if (Visibility.hasSchoolBasedVisibility(visibility)) {
          return Visibility.isSchoolSpecificVisible(
            q.id,
            schoolVisibilityContext
          );
        }

        return true;
      });
    }
    case "none":
      break;
    default: {
      const _unhandled: never = filter;
      console.error("Unhandled filter type: ", filter);
    }
  }

  return completeQuestions;
}

export function flattenQuestion(
  question: AF.Question<AF.WithId>
): readonly AF.Question<AF.WithId>[] {
  if (!hasOptions(question)) {
    return [question];
  }

  const additionalQuestions = question.options.flatMap(
    (option) => option.additionalQuestions ?? []
  );
  return [question, ...additionalQuestions];
}

/**
 * Use this function to calculate all applicable question for this form, which will include/excluse:
 * - school specific questions.
 * - additional questions from branching based on current answer.
 */
export function getCompleteApplicableQuestions(
  questions: readonly AF.Question<AF.WithId>[],
  answers: Readonly<Answer.FormikValues>,
  selectedSchoolIds: SelectedSchoolIds,
  visibilityContext?: Visibility.VisibilityContext
): readonly AF.Question<AF.WithId>[] {
  const completeQuestions = questions
    .filter((question) => {
      // Filter out archived questions
      if (question.isArchived) {
        return false;
      }

      if (visibilityContext !== undefined) {
        return (
          isQuestionApplicable(question, selectedSchoolIds) &&
          Visibility.isVisible(question.id, visibilityContext)
        );
      }

      return isQuestionApplicable(question, selectedSchoolIds);
    })
    .reduce(
      (
        accumulatedQuestions: AF.Question<AF.WithId>[],
        question: AF.Question<AF.WithId>
      ) => {
        if (
          question.type !== AF.SingleSelectType &&
          question.type !== AF.MultiSelectType
        ) {
          return [...accumulatedQuestions, question];
        }
        const answer = answers[question.id];

        if (answer === undefined) {
          return [...accumulatedQuestions, question];
        }

        const additionalQuestions = getAdditionalQuestions(question, answer);
        // Filter out archived additional questions
        const nonArchivedAdditionalQuestions = additionalQuestions.filter(
          (q) => !q.isArchived
        );
        return [
          ...accumulatedQuestions,
          question,
          ...nonArchivedAdditionalQuestions,
        ];
      },
      []
    );

  return completeQuestions;
}

function getAdditionalQuestions(
  question: AF.SingleSelect<AF.WithId> | AF.MultiSelect<AF.WithId>,
  answer: Answer.FormikFieldValue
) {
  let additionalQuestions: AF.Question<AF.WithId>[] = [];
  if (typeof answer === "string") {
    const selectedOption = question.options.find((o) => o.id === answer);

    additionalQuestions = selectedOption?.additionalQuestions ?? [];
  }

  if (Array.isArray(answer)) {
    const selectedOptions = question.options.filter((o) =>
      answer.includes(o.id)
    );

    additionalQuestions = selectedOptions.flatMap(
      (o) => o.additionalQuestions ?? []
    );
  }

  return additionalQuestions;
}

export function findRequiredQuestionIds(
  questions: readonly AF.Question<AF.WithId>[]
): readonly string[] {
  return questions.filter((q) => q.requirement === "Required").map((q) => q.id);
}

export function validateDate(
  value: string | undefined,
  constraints?: DateTypeConstraints | null
): string | undefined {
  if (!value) return undefined; // don't need to validate blank answer

  if (!isValidDate(value)) {
    return "Please enter a valid date";
  }

  const minDate = constraints?.startDate || "1900-01-01";
  const maxDate = constraints?.endDate || "2200-12-31";

  if (DateFns.isBefore(value, minDate)) {
    return `Date must be on or after ${minDate}`;
  } else if (DateFns.isAfter(value, maxDate)) {
    return `Date must be on or before ${maxDate}`;
  }
  return undefined;
}

export function validateNumber(
  value: string | number | undefined,
  constraints?: NumberTypeConstraints
): string | undefined {
  if (value === undefined || value === null) return undefined; // don't need to validate blank answer
  const numValue =
    typeof value === "string"
      ? parseFloat(value.replace(/[^0-9.-]/g, ""))
      : value;

  if (isNaN(numValue)) return undefined;

  const minValue =
    constraints?.minValue !== undefined && constraints?.minValue !== null
      ? Number(constraints.minValue)
      : null;
  const maxValue =
    constraints?.maxValue !== undefined && constraints?.maxValue !== null
      ? Number(constraints.maxValue)
      : null;

  if (minValue !== null && numValue < minValue) {
    return `Number must be greater than or equal to ${minValue}`;
  } else if (maxValue !== null && numValue > maxValue) {
    return `Number must be less than or equal to ${maxValue}`;
  }
  return undefined;
}

export function findFailedConstraintsQuestionIds(
  questions: readonly AF.Question<AF.WithId>[],
  answers: Readonly<Answer.FormikValues>
): readonly string[] {
  return questions.reduce<string[]>((failedIds, question) => {
    const answer = answers[question.id];

    switch (question.type) {
      case AF.DateType: {
        if (!answer) return failedIds;
        const error = validateDate(answer as string, question.constraints);
        if (error) {
          failedIds.push(question.id);
        }
        break;
      }
      case AF.NumberType: {
        if (!answer || !question.constraints) return failedIds;
        const error = validateNumber(
          answer as string | number,
          question.constraints
        );
        if (error) {
          failedIds.push(question.id);
        }
        break;
      }
    }

    return failedIds;
  }, []);
}

export function findEmailQuestionIds(
  questions: readonly AF.Question<AF.WithId>[]
): readonly string[] {
  return questions.filter((q) => q.type === AF.EmailType).map((q) => q.id);
}

export function findPhoneNumberQuestionIds(
  questions: readonly AF.Question<AF.WithId>[]
): readonly string[] {
  return questions
    .filter((q) => q.type === AF.PhoneNumberType)
    .map((q) => q.id);
}

export function findAddressQuestionIds(
  questions: readonly AF.Question<AF.WithId>[]
): readonly string[] {
  return questions.filter((q) => q.type === AF.AddressType).map((q) => q.id);
}

export function findCustomQuestionIds(
  questions: readonly AF.Question<AF.WithId>[]
): readonly string[] {
  return questions
    .filter((q) => q.type === AF.CustomQuestionType)
    .map((q) => q.id);
}

export function hasAnswer(
  answers: Readonly<Answer.FormikValues>,
  questionId: string
): boolean {
  const answer = answers[questionId];
  if (answer === undefined || answer === null) return false;
  if (typeof answer === "string" && answer.trim() === "") return false;
  if (Array.isArray(answer) && answer.length === 0) return false;

  // Check if address answer is valid
  if (
    typeof answer === "object" &&
    Object.keys(answer).includes("street_address")
  ) {
    const { street_address, city, state, zip_code } = answer as BaseAddress;

    return !!(street_address && city && state && zip_code);
  }

  return true;
}

export type SaveStatus = "NotSaving" | "Saving" | "Saved";
export type SaveStatuses = {
  [questionId: uuid]: SaveStatus;
};

export function getSaveStatus(saveStatuses: SaveStatuses): SaveStatus {
  return Object.keys(saveStatuses).reduce(
    (previousStatus: SaveStatus, formId: uuid) => {
      const currentStatus = saveStatuses[formId] ?? "NotSaving";
      if (currentStatus === "NotSaving") {
        return previousStatus;
      }

      if (currentStatus === "Saving") {
        return "Saving";
      }

      if (previousStatus === "Saving") {
        return "Saving";
      }

      return "Saved";
    },
    "NotSaving"
  );
}

export function getAllAdditionalQuestions(
  question: GQL.QuestionFragment
): GQL.FormQuestionWithoutBranchingFragment[] {
  return [
    ...(question.form_question?.form_question_options.flatMap((option) =>
      option.additional_questions.map((aq) => aq.question)
    ) ?? []),
    ...(question.grades_question?.grades_additional_questions.flatMap(
      (gaq) => gaq.question
    ) ?? []),
    ...(question.custom_question?.custom_question_relationships.flatMap(
      (cqr) => cqr.cloned_question
    ) ?? []),
  ].filter(Boolean);
}

export function includeAllAdditionalQuestions(
  questions: GQL.QuestionFragment[]
): GQL.FormQuestionWithoutBranchingFragment[] {
  return questions.flatMap((q) => [q, ...getAllAdditionalQuestions(q)]);
}

export type VerificationQuestions = {
  verification?: AF.FormVerification<AF.WithId>;
  questions: AF.Question<AF.WithId>[];
};

export function groupByVerifications(
  questions: readonly AF.Question<AF.WithId>[]
): VerificationQuestions[] {
  const result: VerificationQuestions[] = [];

  questions.forEach((q) => {
    const verification = (q as AF.FormQuestion<AF.WithId>).formVerification;
    if (!verification) {
      result.push({ questions: [q] });
      return;
    }
    const found = result.find((vq) => vq.verification?.id === verification.id);

    if (!found) {
      result.push({
        verification,
        questions: [q],
      });
    } else {
      found.questions.push(q);
    }
  });
  return result;
}

export function toFormVerification(
  value: GQL.FormQuestionWithoutBranchingFragment
): AF.FormVerification<AF.WithId> | undefined {
  const form_verification = value.form_verification;

  if (!form_verification) return undefined;

  return {
    id: form_verification.id,
    label: form_verification.label,
    is_avela_ai_assisted: form_verification.is_avela_ai_assisted,
    is_identity_ai_assisted: form_verification.is_identity_ai_assisted,
  };
}

export function findInapplicableQuestionsToDelete(
  allQuestions: readonly AF.Question<AF.WithId>[],
  applicableQuestions: readonly AF.Question<AF.WithId>[],
  answers: Readonly<Answer.FormikValues>,
  visibilityContext: Visibility.VisibilityContext
): uuid[] {
  /**
   * We're excluding file upload from deletion for various reason:
   * - The mechanism for triggering deletion autosave doesn't work for file upload.
   * - It's more desirable to keep the file to avoid reupload by user
   * - File upload is not showing in file export, so we don't need to worry about filtering on export.
   * - File upload can't be associated with badge, so no worry about incorrect badge.
   *
   * We're also excluding archived questions - they should keep their answers even though hidden.
   * This aligns with the archive dialog message: "Data will not be deleted and will continue to be visible in exports."
   */
  const ignoreList = new Set(
    allQuestions
      .filter((q) => {
        return q.type === "FileUpload" || q.isArchived;
      })
      .map((q) => q.id)
  );
  const applicableQuestionIds = new Set(applicableQuestions.map((q) => q.id));
  return allQuestions
    .map((q) => q.id)
    .filter((questionId) => {
      if (ignoreList.has(questionId)) {
        return false;
      }
      const hasAnswer = !!answers[questionId];

      return (
        hasAnswer &&
        !applicableQuestionIds.has(questionId) &&
        !Visibility.isSchoolSpecificQuestion(questionId, visibilityContext) &&
        !Visibility.answerIsVerified(questionId, visibilityContext)
      );
    });
}

export function getSpecificToSchoolsQuestions(
  questions: readonly AF.Question<AF.WithId>[],
  visibilityContext: Pick<
    Visibility.VisibilityContext,
    "selectedSchoolsSet" | "visibilityMap"
  >
): AF.Question<AF.WithId>[] {
  return questions.filter((question) => {
    const isSchoolSpecificVisibility = Visibility.isSchoolSpecificVisible(
      question.id,
      visibilityContext
    );

    if (isSchoolSpecificVisibility) {
      return true;
    }

    const isSchoolSpecificQuestion =
      question.specificToSchools !== undefined &&
      question.specificToSchools.some((specificToSchoolId) =>
        visibilityContext.selectedSchoolsSet.has(specificToSchoolId)
      );

    if (isSchoolSpecificQuestion) {
      return true;
    }

    return false;
  });
}

export function hasOptions<T extends AF.WithId | AF.WithoutId>(
  question: Pick<AF.Question<T>, "type"> | { type: "" } | undefined
): question is
  | Pick<AF.SingleSelect<T>, "type">
  | Pick<AF.Grades<T>, "type">
  | Pick<AF.MultiSelect<T>, "type"> {
  if (question === undefined) {
    return false;
  }

  switch (question.type) {
    case AF.MultiSelectType:
    case AF.SingleSelectType:
    case AF.GradesType:
      return true;
    case AF.DateType:
    case AF.NumberType:
    case AF.FileUploadType:
    case AF.FreeTextType:
    case AF.EmailType:
    case AF.PhoneNumberType:
    case AF.AddressType:
    case AF.CustomQuestionType:
    case "":
      return false;

    default:
      const _exhaustiveCheck: never = question.type;
      return _exhaustiveCheck;
  }
}

export function hasCustomQuestionType<T extends AF.WithId | AF.WithoutId>(
  question: Pick<AF.Question<T>, "type"> | { type: "" } | undefined
): question is Pick<AF.CustomQuestion<T>, "type"> {
  if (question === undefined) {
    return false;
  }

  switch (question.type) {
    case AF.CustomQuestionType:
      return true;
    case AF.DateType:
    case AF.NumberType:
    case AF.MultiSelectType:
    case AF.SingleSelectType:
    case AF.FileUploadType:
    case AF.FreeTextType:
    case AF.GradesType:
    case AF.EmailType:
    case AF.PhoneNumberType:
    case AF.AddressType:
    case "":
      return false;

    default:
      const _exhaustiveCheck: never = question.type;
      return _exhaustiveCheck;
  }
}

export function toQuestionWithoutId(
  question: AF.Question<AF.WithId>
): AF.Question<AF.WithoutId> {
  const { id, ...questionWithoutId } = question;
  switch (questionWithoutId.type) {
    case AF.SingleSelectType:
    case AF.MultiSelectType:
      return {
        ...questionWithoutId,
        options: questionWithoutId.options.map(toOptionWithoutId),
      };

    case AF.GradesType:
      return {
        ...questionWithoutId,
        gradesAdditionalQuestions: [], //TODO deal with additional question in future task.
      };

    case AF.CustomQuestionType:
      return { ...questionWithoutId, nestedQuestions: [] }; // CQT-TODO

    case AF.DateType:
    case AF.NumberType:
    case AF.FileUploadType:
    case AF.FreeTextType:
    case AF.EmailType:
    case AF.PhoneNumberType:
    case AF.AddressType:
      return { ...questionWithoutId };

    default:
      const _exhaustiveCheck: never = questionWithoutId;
      return _exhaustiveCheck;
  }
}

export function toOptionWithoutId(
  option: AF.Option<AF.WithId>
): AF.Option<AF.WithoutId> {
  const { id, additionalQuestions, ...optionWithoutId } = option;
  return {
    ...optionWithoutId,
    additionalQuestions: additionalQuestions?.map(toQuestionWithoutId),
  };
}

export function findGradesQuestion(
  questions: readonly AF.Question<AF.WithId>[]
): AF.Grades<AF.WithId> | undefined {
  const completeQuestions = getCompleteQuestions(questions);
  return completeQuestions.find(
    (question): question is AF.Grades<AF.WithId> =>
      question.type === AF.GradesType
  );
}

export function findGeoEligibilityQuestion(
  questions: readonly AF.Question<AF.WithId>[]
): AF.Address<AF.WithId> | undefined {
  const completeQuestions = getCompleteQuestions(questions);
  return completeQuestions.find(isGeoEligibilityQuestion);
}

export function isGeoEligibilityQuestion(
  question: AF.Question<AF.WithId>
): question is AF.Address<AF.WithId> {
  if (question.type !== AF.AddressType) {
    return false;
  }

  const filter = question.filters?.find(isLocationBoundariesFilter);
  if (filter === undefined) {
    return false;
  }

  if (filter.config.rule === LocationBoundariesFilterRuleOptions.ShowFirst) {
    return false;
  }

  return true;
}

export function findCustomQuestion(
  questions: readonly AF.Question<AF.WithId>[]
): AF.CustomQuestion<AF.WithId> | undefined {
  const completeQuestions = getCompleteQuestions(questions);
  return completeQuestions.find(
    (question): question is AF.CustomQuestion<AF.WithId> =>
      question.type === AF.CustomQuestionType
  );
}

export function toBaseQuestion(
  value: GQL.FormQuestionWithoutBranchingFragment
): Omit<AF.Question<AF.WithId>, "type" | "specificToSchools"> {
  return {
    id: value.id,
    key: value.key ?? undefined,
    question: value.question,
    requirement: value.is_required ? "Required" : undefined,
    link_text: value.link_text ?? undefined,
    link_url: value.link_url ?? undefined,
    permissionLevel: value.permission_level ?? undefined,
    constraints: value.constraints,
    visibility: VisibilitySchema.nullable().parse(value.visibility),
    isArchived: !!value.archived_at,
  };
}

export {
  findQuestionsNeedingAnswerCleanup,
  isSchoolSpecificQuestion,
  isVisible,
} from "./visibility";
